/**
 * StammdatenTab — Tab 1 im FahrzeugDetailDrawer
 *
 * Read-only Anzeige der Stammdaten eines Fahrzeugs + inline editierbarer
 * km-Stand. Holt beim Mount nur die Stammdaten-Felder und rendert sie via
 * `buildStammdatenRows`. km-Stand wird separat behandelt (Edit-Modus mit
 * persistentem Update). Skeleton-Loader während Fetch.
 *
 * Part of Issue #185 / Parent #183.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import {
  buildStammdatenRows,
  formatKm,
  formatKmAktualisiert,
  type FahrzeugStammdaten,
} from '@/lib/stammdaten-tab';

interface StammdatenTabProps {
  fahrzeug: { id: string };
  accent: string;
  onKmUpdated?: () => void;
}

const STAMMDATEN_COLUMNS = [
  'kennzeichen',
  'fin',
  'hsn',
  'tsn',
  'erstzulassung',
  'kraftstoffart',
  'farbe',
  'kw',
  'hubraum_ccm',
  'batteriekapazitaet_kwh',
  'wltp_reichweite_km',
  'km_stand',
  'km_stand_aktualisiert_am',
].join(', ');

export default function StammdatenTab({ fahrzeug, accent, onKmUpdated }: StammdatenTabProps) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<FahrzeugStammdaten | null>(null);
  const [editingKm, setEditingKm] = useState(false);
  const [kmDraft, setKmDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchStammdaten = useCallback(async () => {
    setLoading(true);
    const { data: row, error } = await supabase
      .from('fahrzeuge')
      .select(STAMMDATEN_COLUMNS)
      .eq('id', fahrzeug.id)
      .maybeSingle();
    if (error) {
      console.error('Stammdaten-Tab: fetch failed', error);
      toast.error('Fehler beim Laden der Stammdaten');
      setData(null);
      setLoading(false);
      return;
    }
    setData((row as unknown as FahrzeugStammdaten) ?? null);
    setLoading(false);
  }, [fahrzeug.id]);

  useEffect(() => {
    void fetchStammdaten();
  }, [fetchStammdaten]);

  useEffect(() => {
    if (editingKm) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editingKm]);

  const startEdit = () => {
    setKmDraft(data?.km_stand != null ? String(data.km_stand) : '');
    setEditingKm(true);
  };

  const cancelEdit = () => {
    setEditingKm(false);
    setKmDraft('');
  };

  const saveKm = async () => {
    const raw = kmDraft.trim().replace(/\./g, '');
    if (raw === '' || Number.isNaN(Number(raw))) {
      toast.error('Bitte eine gültige Kilometerzahl eingeben');
      return;
    }
    const newKm = Math.max(0, Math.round(Number(raw)));
    setSaving(true);
    const nowIso = new Date().toISOString();
    const { error } = await supabase
      .from('fahrzeuge')
      .update({ km_stand: newKm, km_stand_aktualisiert_am: nowIso })
      .eq('id', fahrzeug.id);
    setSaving(false);
    if (error) {
      console.error('Stammdaten-Tab: km-update failed', error);
      toast.error('Kilometerstand konnte nicht gespeichert werden');
      return;
    }
    toast.success('Kilometerstand aktualisiert');
    setData((prev) =>
      prev ? { ...prev, km_stand: newKm, km_stand_aktualisiert_am: nowIso } : prev,
    );
    setEditingKm(false);
    setKmDraft('');
    onKmUpdated?.();
  };

  if (loading) {
    return (
      <div className="space-y-2" data-testid="stammdaten-skeleton">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-8 rounded-lg bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="text-center py-12 text-sm text-gray-400"
        data-testid="stammdaten-empty"
      >
        Keine Stammdaten für dieses Fahrzeug hinterlegt
      </div>
    );
  }

  const rows = buildStammdatenRows(data);

  return (
    <div className="space-y-4" data-testid="stammdaten-content">
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="stammdaten-grid">
        {rows.map((row) => (
          <div key={row.label}>
            <dt className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
              {row.label}
            </dt>
            <dd className="text-sm text-gray-700 font-medium">{row.value}</dd>
          </div>
        ))}
      </dl>

      <div
        className="rounded-xl border border-gray-100 p-3"
        style={{ backgroundColor: 'rgba(0,0,0,0.015)' }}
        data-testid="stammdaten-km"
      >
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: accent }}>
            Kilometerstand
          </div>
          {!editingKm && (
            <button
              type="button"
              onClick={startEdit}
              className="inline-flex items-center gap-1 text-[11px] font-bold"
              style={{ color: accent }}
              data-testid="stammdaten-km-edit"
            >
              <Pencil className="w-3 h-3" /> Bearbeiten
            </button>
          )}
        </div>
        {editingKm ? (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="number"
              min="0"
              inputMode="numeric"
              value={kmDraft}
              onChange={(e) => setKmDraft(e.target.value)}
              disabled={saving}
              className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2"
              style={{ outlineColor: accent }}
              data-testid="stammdaten-km-input"
              placeholder="km-Stand"
            />
            <button
              type="button"
              onClick={() => void saveKm()}
              disabled={saving}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white disabled:opacity-50"
              style={{ backgroundColor: accent }}
              data-testid="stammdaten-km-save"
              aria-label="Kilometerstand speichern"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              aria-label="Abbrechen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-baseline justify-between">
            <div className="text-lg font-bold text-gray-900" data-testid="stammdaten-km-value">
              {formatKm(data.km_stand)}
            </div>
            <div className="text-[11px] text-gray-400" data-testid="stammdaten-km-aktualisiert">
              {formatKmAktualisiert(data.km_stand_aktualisiert_am)}
            </div>
          </div>
        )}
      </div>

      {rows.length === 0 && (
        <div className="text-center py-6 text-xs text-gray-400">
          Weitere Stammdaten sind noch nicht gepflegt
        </div>
      )}
    </div>
  );
}
